<template>
  <v-container>
    <v-card outlined>
      <v-card-title class="d-flex justify-space-between align-center pa-4">
        <span>{{ fileUrl ? truncateTitle(fileName) : titleName }}</span>
        <v-chip
          v-if="required"
          color="error"
          small
          class="ml-2"
        >
          Obrigatório
        </v-chip>
      </v-card-title>

      <v-card-text>
        <v-hover v-slot="{ hover }">
          <v-card
              :class="{ 'on-hover': hover }"
              class="d-flex justify-center align-center"
              height="200"
              outlined
              :style="disabled ? 'cursor: not-allowed;' : 'cursor: pointer;'"
              @click="handleCardClick"
          >
            <v-overlay :value="loading" absolute style="opacity: 0.8">
              <v-progress-circular :size="50" color="primary" indeterminate></v-progress-circular>
            </v-overlay>

            <template v-if="isImage">
              <v-img
                  :src="filePreview"
                  alt="Pré-visualização da imagem"
                  class="image-preview"
                  contain
              ></v-img>
            </template>
            <template v-else-if="isPdf || isDocx">
              <div class="d-flex flex-column align-center justify-center">
                <v-icon
                    size="100"
                    :color="isPdf ? 'red' : 'blue'"
                >
                  {{ isPdf ? 'mdi-file-pdf-box' : 'mdi-file-word-box' }}
                </v-icon>
                <span class="subtitle-1 mt-3">{{ truncateTitle(fileName) }}</span>
                <span class="caption grey--text mt-1">{{ formatFileSize(fileSize) }}</span>
              </div>
            </template>
            <template v-else-if="!fileUrl">
              <div class="text-center">
                <v-icon color="grey" size="64">mdi-file-upload-outline</v-icon>
                <div class="subtitle-1 grey--text">{{ helperText }}</div>
                <div class="caption grey--text mt-2">
                  Formatos aceitos: {{ formatAcceptedTypes }}
                </div>
                <div class="caption grey--text">
                  Tamanho máximo: {{ formatFileSize(maxFileSize) }}
                </div>
              </div>
            </template>
          </v-card>
        </v-hover>

        <v-alert
          v-if="errorMessage"
          type="error"
          class="mt-3"
          dense
          dismissible
          @click.stop="errorMessage = ''"
        >
          {{ errorMessage }}
        </v-alert>

        <input
            ref="fileInput"
            :accept="computedAccept"
            style="display: none;"
            type="file"
            :disabled="disabled"
            @change="handleFileUpload"
        />
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import compressor from "@/utils/CompressorFile.js";

export default {
  props: {
    refInput: {
      type: Number,
      default: 1111,
    },
    titleName: {
      type: String,
      default: 'Anexar Arquivo'
    },
    helperText: {
      type: String,
      default: 'Clique para selecionar um arquivo'
    },
    compressorConfig: {
      type: Object,
      default: () => ({
        quality: 1.0,
        maxWidth: 800,
        maxHeight: 800,
        convertSize: 0,
        mimeType: "image/webp",
      }),
    },
    title: {
      type: String,
      default: "Adicione um arquivo",
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showFile: {
      type: [Object, Blob],
      default: () => ({}),
    },
    fileTypes: {
      type: Array,
      default: () => [],
    },
    required: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Array,
      default: () => [],
    },
    maxFileSize: {
      type: Number,
      default: 10 * 1024 * 1024, // 10MB default
    },
  },
  data() {
    return {
      file: null,
      filePreview: null,
      fileUrl: null,
      errorMessage: '',
    };
  },
  computed: {
    computedAccept() {
      const accept = [];
      if (this.fileTypes.includes("image")) accept.push("image/*");
      if (this.fileTypes.includes("pdf")) accept.push(".pdf");
      if (this.fileTypes.includes("docx")) {
        accept.push(".docx")
        accept.push("application/vnd.openxmlformats-officedocument.wordprocessingml.document")
      }
      return accept.join(",");
    },
    isImage() {
      return this.file && this.file.type.startsWith("image/");
    },
    fileName() {
      return this.file && this.file.name ? this.file.name : "";
    },
    fileSize() {
      return this.file ? this.file.size : 0;
    },
    isPdf() {
      return this.file && this.file.type === "application/pdf";
    },
    isDocx() {
      return this.file && (
          this.file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
          this.file.name.endsWith('.docx')
      );
    },
    formatAcceptedTypes() {
      const types = [];
      if (this.fileTypes.includes("image")) types.push("Imagens");
      if (this.fileTypes.includes("pdf")) types.push("PDF");
      if (this.fileTypes.includes("docx")) types.push("DOCX");
      return types.join(", ");
    },
  },
  methods: {
    // Função centralizada para limpar URLs de objeto
    clearObjectURLs() {
      if (this.fileUrl) URL.revokeObjectURL(this.fileUrl);
      if (this.filePreview) URL.revokeObjectURL(this.filePreview);
    },
    truncateTitle(text, maxLength = 39) {
      if (text && text.length > maxLength) {
        return text.substring(0, maxLength) + '...';
      }
      return text;
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    handleCardClick() {
      if (!this.disabled) {
        this.$refs.fileInput.click();
      }
    },
    validateFile(file) {
      // Check file size
      if (file.size > this.maxFileSize) {
        this.errorMessage = `O arquivo é muito grande. Tamanho máximo permitido: ${this.formatFileSize(this.maxFileSize)}`;
        return false;
      }

      // Check file type
      const isValidType = this.fileTypes.some(type => {
        if (type === "image") return file.type.startsWith("image/");
        if (type === "pdf") return file.type === "application/pdf";
        if (type === "docx") {
          return file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                 file.name.endsWith('.docx');
        }
        return false;
      });

      if (!isValidType) {
        this.errorMessage = `Tipo de arquivo não permitido. Formatos aceitos: ${this.formatAcceptedTypes}`;
        return false;
      }

      return true;
    },
    async handleFileUpload(event) {
      const newFile = event.target.files[0];

      if (!newFile) return;

      this.clearFile();
      this.errorMessage = '';

      if (!this.validateFile(newFile)) {
        this.clearFile();
        return;
      }

      this.file = newFile;

      try {
        if (this.isImage) {
          this.file = await compressor.compressorFile(this.file, this.compressorConfig);
          this.filePreview = URL.createObjectURL(this.file);
        } else if (this.isPdf || this.isDocx) {
          this.filePreview = null;
        } else {
          this.errorMessage = "Tipo de arquivo inválido.";
          this.clearFile();
          return;
        }

        this.fileUrl = URL.createObjectURL(this.file);
        this.$emit("change:file", this.file);
      } catch (error) {
        console.error("Erro ao processar arquivo:", error);
        this.errorMessage = "Erro ao processar o arquivo. Por favor, tente novamente.";
        this.clearFile();
      }
    },
    clearFile() {
      this.clearObjectURLs();

      this.file = null;
      this.fileUrl = null;
      this.filePreview = null;
      this.errorMessage = '';

      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = null;
      }

      this.$emit("change:file", this.file);
    },
  },
  watch: {
    showFile: {
      immediate: true,
      handler(newValue) {
        if (!(newValue instanceof Blob || newValue instanceof File)) {
          this.clearFile();
          return;
        }

        this.clearFile();

        if (!this.validateFile(newValue)) {
          this.clearFile();
          return;
        }

        this.file = newValue;
        this.fileUrl = URL.createObjectURL(newValue);

        if (this.isImage) {
          this.filePreview = URL.createObjectURL(newValue);
        } else if (this.isPdf || this.isDocx) {
          this.filePreview = null;
        } else {
          this.errorMessage = "Tipo de arquivo inválido.";
          this.clearFile();
        }
        this.$emit("change:file", this.file);
      },
    },
  },
};
</script>

<style scoped>
.image-preview {
  object-fit: contain;
  max-width: 100%;
  max-height: 100%;
  height: auto;
  width: auto;
}

.on-hover {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px dashed var(--v-primary-base) !important;
}
</style>
